import { useQueryTemplateCreationRecordById } from '@/features/templateBuilder/hooks/queries/useQueryTemplateCreationRecordById';
import { useStructureStreamState } from '@/features/templateBuilder/hooks/useStructureStreamState';
import { templateCreationRecordIdAtom } from '@heidi/templates';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';

import type { Keyword, LoaderPhase } from '@heidi/templates';
import { extractKeywords } from '@heidi/templates';

// Phase durations in milliseconds
const PHASE_DURATIONS_MS = {
  understanding: 6000, // 6 seconds for floating keywords
  building: 6000, // 6 seconds for structure assembly
};

interface UseLoaderPhaseResult {
  phase: LoaderPhase;
  userInput: string;
  keywords: Keyword[];
}

export function useLoaderPhase(): UseLoaderPhaseResult {
  const { isIdle: isStructureIdle } = useStructureStreamState();

  // If structure is idle, skip to drafting (only example is being generated)
  const [phase, setPhase] = useState<LoaderPhase>(
    isStructureIdle ? 'drafting' : 'understanding'
  );

  const templateCreationRecordId = useAtomValue(templateCreationRecordIdAtom);
  const { data } = useQueryTemplateCreationRecordById({
    templateCreationRecordId,
    select: (data) => ({
      request: data.template_creation_record.request ?? '',
    }),
  });

  const userInput = data?.request ?? '';

  const keywords = useMemo(() => {
    return extractKeywords(userInput, 5);
  }, [userInput]);

  // Auto-progress through phases on mount (only if structure is not idle)
  useEffect(() => {
    // If structure is idle, we start at drafting - no timers needed
    if (isStructureIdle) {
      setPhase('drafting');
      return;
    }

    const buildingTimer = setTimeout(() => {
      setPhase('building');
    }, PHASE_DURATIONS_MS.understanding);

    const draftingTimer = setTimeout(() => {
      setPhase('drafting');
    }, PHASE_DURATIONS_MS.understanding + PHASE_DURATIONS_MS.building);

    return () => {
      clearTimeout(buildingTimer);
      clearTimeout(draftingTimer);
    };
  }, [isStructureIdle]);

  return {
    phase,
    userInput,
    keywords,
  };
}
